'use client';

import { useState, useEffect } from 'react';
import { MessageSquare, X, Minimize2, Maximize2 } from 'lucide-react';

export default function FloatingChatDemo() {
  // 移除鼠标位置跟踪
  const [windowHeight, setWindowHeight] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  
  // 只保留窗口大小调整的监听
  useEffect(() => {
    // Set initial window height
    setWindowHeight(window.innerHeight);
    
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const messages = [
    {
      role: "user",
      content: "What can ReasoningAI do?",
    },
    {
      role: "assistant",
      content:
        "ReasoningAI is an advanced reasoning solver that excels at logical reasoning, symbolic reasoning, and automated reasoning tasks. Our models can solve complex problems across various domains including mathematics, science, programming, and business logic. As one of the best reasoning AI tools online, we offer both free and premium solutions for all your reasoning needs.",
    },
    {
      role: "user",
      content: "Can I use it for free?",
    },
    {
      role: "assistant",
      content:
        "Yes! We offer a free reasoning AI tool online that you can try immediately. Our free version gives you access to basic symbolic reasoning capabilities and logical problem solving. For more advanced features and deep reasoning AI capabilities, check out our premium plans at reasoningai.com.",
    },
    {
      role: "user",
      content: "How does it compare to other reasoning systems?",
    },
    {
      role: "assistant",
      content:
        "ReasoningAI is considered the best AI for logical reasoning by many researchers and professionals. Unlike other systems, our reasoning AI models combine multiple approaches - including logical, symbolic, and deep reasoning - to provide more accurate and reliable solutions. Our reasoning solver AI can tackle problems that other systems struggle with.",
    },
  ];

  const [visibleMessages, setVisibleMessages] = useState<any[]>([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [typingText, setTypingText] = useState("");
  const [typingIndex, setTypingIndex] = useState(0);

  useEffect(() => {
    if (currentMessageIndex < messages.length) {
      if (currentMessageIndex % 2 === 0) {
        // For user messages, just add them immediately
        setVisibleMessages((prev) => [...prev, messages[currentMessageIndex]]);
        setCurrentMessageIndex((prev) => prev + 1);
      } else {
        // For assistant messages, simulate typing
        setIsTyping(true);
        setTypingText("");
        setTypingIndex(0);

        const message = messages[currentMessageIndex];
        const typingInterval = setInterval(() => {
          setTypingText((prev) => {
            const nextChar = message.content[typingIndex];
            return prev + nextChar;
          });
          setTypingIndex((prev) => prev + 1);

          if (typingIndex >= message.content.length - 1) {
            clearInterval(typingInterval);
            setIsTyping(false);
            setVisibleMessages((prev) => [...prev, message]);
            setTimeout(() => {
              setCurrentMessageIndex((prev) => prev + 1);
            }, 1000);
          }
        }, 30);

        return () => clearInterval(typingInterval);
      }
    }
  }, [currentMessageIndex, typingIndex]);

  return (
    <div 
      className={`fixed right-4 z-40 transition-all duration-300 ease-out ${isMinimized ? 'w-12' : 'w-80'}`}
      style={{ 
        top: '80px', // 固定在顶部下方80px的位置
        opacity: isMinimized ? 1 : 0.85,
      }}
    >
      {isMinimized ? (
        <button 
          onClick={toggleMinimize}
          className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg hover:bg-blue-700 transition-colors"
        >
          <MessageSquare size={20} />
        </button>
      ) : (
        <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-blue-100 hover:opacity-100 transition-opacity">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-gray-700">AI Assistant</span>
            <button 
              onClick={toggleMinimize}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Minimize2 size={16} />
            </button>
          </div>
          <div className="chat-container space-y-4">
            <div className="chat-item">
              {visibleMessages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${
                    message.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`p-3 rounded-lg ${
                      message.role === "user"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-800"
                    } max-w-[80%]`}
                  >
                    <p>{message.content}</p>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="p-3 rounded-lg bg-gray-200 text-gray-800 max-w-[80%]">
                    <p className="typing-text text-sm">{typingText}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}